import { Button, Text } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useRef } from 'react';

import { IMAGE_FILE_INPUT_ACCEPT } from '@/api/fileUploadApi';
import { BlobImage } from '@/components/blob/BlobImage';
import { cn } from '@/utils/cn';

import type { InspirationReference } from '../../../../bridge/types/artifact.ts';

import { EmptyMessage } from './EmptyMessage';
import { formatInspirationMeta } from './inspirationFormatting';
import { useInspirationUpload } from './useInspirationUpload';

const cardTitleStyles = style({
	font: 'title',
	fontSize: 'title-sm',
	fontWeight: 'bold',
	color: 'title',
});

const cardMetaStyles = style({
	font: 'body',
	fontSize: 'body-sm',
	color: 'body',
});

interface InspirationLibraryProps {
	inspirations: InspirationReference[];
	onClickInspiration?: (artifact: InspirationReference) => void;
}

export const InspirationLibrary = function InspirationLibrary({
	inspirations,
	onClickInspiration,
}: InspirationLibraryProps) {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { uploadAndSave, isUploading, error } = useInspirationUpload();

	// One control, shown whether or not the library has anything in it. A
	// customer with a full library still needs a way to add the next one.
	const uploadControl = (
		<div className="flex flex-col gap-1">
			<Button
				variant="secondary"
				isDisabled={isUploading}
				onPress={() => fileInputRef.current?.click()}
			>
				{isUploading ? 'Reading the design…' : 'Upload a design'}
			</Button>
			{error !== null && <Text styles={cardMetaStyles}>{error}</Text>}
			<input
				ref={fileInputRef}
				type="file"
				accept={IMAGE_FILE_INPUT_ACCEPT}
				className="hidden"
				data-testid="inspiration-upload-input"
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) void uploadAndSave(file);
					// Clear the value so choosing the same file again still fires.
					e.target.value = '';
				}}
			/>
		</div>
	);

	return (
		// A tab panel now, not a band stacked under the content grid: it owns the
		// full height it is given, and the scroll lives on the inner region so the
		// tab header above stays put.
		<div
			className="flex h-full min-h-0 flex-col gap-6"
			data-testid="inspiration-library"
		>
			<div className="flex justify-end">{uploadControl}</div>
			{inspirations.length === 0 ? (
				<EmptyMessage content="No saved inspiration yet. Upload a picture of a post you like, or share a public Instagram, TikTok, or YouTube link in chat or by text, and I'll analyze it." />
			) : (
				<div className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto pb-8">
					<div className="grid grid-cols-2 gap-x-4 gap-y-6 md:grid-cols-3">
						{inspirations.map((artifact) => {
							const title = artifact.content.topic || 'Saved inspiration';
							return (
								<button
									key={artifact.id}
									type="button"
									data-testid={`inspiration-tile-${artifact.id}`}
									onClick={() => onClickInspiration?.(artifact)}
									className="group flex cursor-pointer flex-col gap-2 text-left"
								>
									<BlobImage
										src={artifact.content.thumbnail_url}
										alt={title}
										aria-label={title}
										// Thumbnails point at platform CDNs whose URLs expire, so a
										// missing or dead image has to degrade to a placeholder
										// rather than a broken tile.
										fallback={{}}
										className={cn(
											'aspect-square h-full w-full rounded-lg object-cover',
											'opacity-90 transition-opacity group-hover:opacity-100',
										)}
									/>
									<div className="flex flex-col gap-1">
										<div className={cn('line-clamp-2', cardTitleStyles)}>
											{title}
										</div>
										<Text styles={cardMetaStyles}>
											{formatInspirationMeta(artifact.content)}
										</Text>
									</div>
								</button>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};
